import { useRef } from 'react';

import useActions from '@app/store/hooks/useActions';
import { useTypedDispatch } from '@app/store/hooks/useTypedDispatch';

import { routingConfigVersionApi, useCreateRoutingConfigDraftMutation } from '../api';
import { GENERIC_RESTORE_ERROR, parseConfigPathErrors } from '../lib/configErrors';
import { nextAvailableName } from '../lib/versionNames';
import { IConfigIssue, IRoutingConfigVersion } from '../model/types';

export type TRestoreResult =
  | { status: 'restored'; draft: IRoutingConfigVersion }
  | { status: 'failed'; issues: IConfigIssue[] };

// Restore is Save Draft's clone step done up front: one draft POST from the archived
// version, then the edit session opens on the created draft, so the operator lands in
// the ordinary draft flow (edit → Save Draft in place → Publish). The archived version
// is only ever read.
export const useRestoreVersion = () => {
  const dispatch = useTypedDispatch();
  const { startEditing } = useActions();
  const [createDraft, { isLoading }] = useCreateRoutingConfigDraftMutation();
  // Names already handed out here, reserved before the POST: the draft POST
  // invalidates no tags, so the fetched list lags until the next refetch, and two
  // restores in flight at once would otherwise both pick the same "(N)".
  const createdNames = useRef<string[]>([]);

  const restoreVersion = async (
    source: IRoutingConfigVersion,
    takenNames: string[],
  ): Promise<TRestoreResult> => {
    // The base stays behind under its own name, so the copy takes the first free
    // numbered variant; the operator can still rename it at Save Draft.
    const name = nextAvailableName(source.name, [
      source.name,
      ...takenNames,
      ...createdNames.current,
    ]);
    createdNames.current.push(name);

    try {
      const response = await createDraft({ name, source_version_id: source.id }).unwrap();
      const draft = response.data;

      // Seed the by-id cache so the switch to ?version={draft} renders from this
      // response — and the editor's stale-session check, keyed on updated_at, reads
      // the same value the GET would return.
      dispatch(
        routingConfigVersionApi.util.upsertQueryData('getRoutingConfigVersion', draft.id, response),
      );
      startEditing(draft);

      return { status: 'restored', draft };
    } catch (error) {
      // No draft got that name after all — free it for the next attempt.
      createdNames.current = createdNames.current.filter(item => item !== name);

      return { status: 'failed', issues: parseConfigPathErrors(error, GENERIC_RESTORE_ERROR) };
    }
  };

  return { restoreVersion, isRestoring: isLoading };
};
